import { useEffect, useMemo, useState } from 'react';
import type { ChatWidgetProps } from '../types';

type CustomerEmailCaptureConfig = ChatWidgetProps['customerEmailCapture'];

export function isValidEmailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function readStoredEmail(enabled: boolean, storageKey: string): string {
  if (!enabled || typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(storageKey) || '';
  } catch {
    return '';
  }
}

export function useCustomerEmailCapture(config: CustomerEmailCaptureConfig) {
  const enabled = !!config?.enabled;
  const required = enabled && config?.required !== false;
  const storageKey = config?.storageKey || 'rw_customer_email';
  const [email, setEmail] = useState(() => readStoredEmail(enabled, storageKey));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setEmail(readStoredEmail(enabled, storageKey));
    setTouched(false);
  }, [enabled, storageKey]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const trimmed = email.trim();
    try {
      if (trimmed) window.localStorage.setItem(storageKey, trimmed);
      else window.localStorage.removeItem(storageKey);
    } catch {
      // Browser storage can be unavailable in private or embedded contexts.
    }
  }, [email, enabled, storageKey]);

  const trimmedEmail = email.trim();
  const isValid = useMemo(() => isValidEmailAddress(trimmedEmail), [trimmedEmail]);

  return {
    email,
    enabled,
    isValid,
    required,
    setEmail,
    setTouched,
    touched,
    trimmedEmail,
  };
}
